import { locHrefUtil, offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { DEV_PARAMS, SALES_VIEW_HOST_NAME } from '../constants';
import { getEnv } from '../host/hostSupport';
import { selectEnv, getDsEnvParam } from '../environment/environmentUtils';

export function getShowRoomUrl(id: string, env: string) {
    const dsEnvParam = getDsEnvParam(env);
    const baseUrl = `${selectEnv(env)}${SALES_VIEW_HOST_NAME}/shopperPage.html`;
    const queryParams = `dealXgId=${id}&route=shopper-quote-dashboard&source=VIN${dsEnvParam}`;
    return `https://${baseUrl}?${queryParams}`;
}
// TODO: Shashank B - merge getShowRoomUrlWithVersion and getShowRoomUrl into one function and verify dealId usage
export function getShowRoomUrlWithVersion(dealId: string, version: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('dealXgId', dealId);
    url.searchParams.set('dealXgVersion', version);
    url.searchParams.set('isShowTradeInModal', 'true');
    return url.toString();
}

export const buildShopperUrl = (dealXgId: string) => {
    const envName = getEnv() === 'local' ? 'dev' : getEnv();
    return getShowRoomUrl(dealXgId, envName);
};

export const initSessionToggles = (href: string, whitelist: string[]): void => {
    offerSessionUtils.parseAndWriteAdditionalToggles(href, { whitelist });
};

/** Pins a version to the deal it belongs to — see getDealXgVersionPin. */
export function setDealXgVersionPin(dealXgId: string, version: string): void {
    offerSessionUtils.setSessionValue('dealXgVersionPin', JSON.stringify({ dealXgId, version }));
}

/** Returns the pinned version only if it was pinned against `currentDealXgId` — never a stale one from a different deal. */
export function getDealXgVersionPin(currentDealXgId: string): string | undefined {
    const raw = offerSessionUtils.getSessionValue('dealXgVersionPin');
    if (!raw) return undefined;
    try {
        const { dealXgId, version } = JSON.parse(raw);
        return dealXgId === currentDealXgId ? version : undefined;
    } catch {
        return undefined;
    }
}

export function deleteDealXgVersionPin(): void {
    offerSessionUtils.deleteSessionValue('dealXgVersionPin');
}

export const persistSearchParams = (search: string): void => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const pr = params.get('pr');
    if (pr) {
        sessionStorage.setItem('cmd_oid_pr', pr);
    }
    // The URL is authoritative for the pin: if this load's dealXgId has a version alongside it,
    // pin that pair; otherwise clear whatever's pinned so bootstrap falls through to latest. This
    // covers a dealXgId change AND a same-deal reload with dealXgVersion edited/removed from the URL.
    const dealXgId = params.get('dealXgId');
    const dealXgVersion = params.get('dealXgVersion');
    if (dealXgId) {
        if (dealXgVersion) {
            setDealXgVersionPin(dealXgId, dealXgVersion);
        } else {
            deleteDealXgVersionPin();
        }
    }
};

/**
 * Points the URL's dealXgVersion param at `version`, without navigating.
 *
 * Advancing the param rather than stripping it is what keeps `persistSearchParams` (just above)
 * harmless — that runs on every render and re-persists this param into session, so a stale value
 * left in the URL would silently undo each version update.
 */
// TODO: visit later for clean approach
export const syncDealXgVersionInUrl = (version: string): void => {
    if (!version || typeof window.location.href !== 'string') return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('dealXgVersion') === version) return;
    url.searchParams.set('dealXgVersion', version);
    window.history.replaceState(window.history.state, '', url.toString());
};

/**
 * Resolves the active SCI query params from the `cmd_oid_*` session store
 * (written by AuthManager and `persistSearchParams`). This keeps the
 * params stable after the Okta sign-in callback "poisons" the URL (`?code=...&state=...`),
 * which strips the offer identifiers (`dealXgId`, OrgID, CCID) and whitelisted toggles.
 */
const getSessionSearchParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    const setIfPresent = (key: string, value?: string | null) => {
        if (value) params.set(key, value);
    };

    // Offer identifiers (dealXgId, OrgID, CCID) and whitelisted toggles (bffEnv, theme, …).
    Object.entries(offerSessionUtils.getOfferIdentifiers()).forEach(([key, value]) => setIfPresent(key, value));
    Object.entries(offerSessionUtils.getAdditionalToggles()).forEach(([key, value]) => setIfPresent(key, value));
    setIfPresent('pr', offerSessionUtils.getSessionValue('pr'));
    // dealXgVersion and isShowTradeInModal are intentionally NOT re-appended to the URL.
    // dealXgVersion is consumed from session by bootstrap; isShowTradeInModal is a one-time
    // entry trigger read from the initial URL by the customer-vehicles middleware.

    return params;
};

export const buildQueryString = (): string => {
    const params = getSessionSearchParams();
    if (getEnv() === 'prod') {
        DEV_PARAMS.forEach((key) => params.delete(key));
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
};

// buildQueryString() returns the canonical session-based query string.
// Strip any existing query from `route` to avoid duplicate params on repeated navigations.
export const appendQueryString = (route: string): string => {
    const [path] = route.split('?');
    return path + buildQueryString();
};

export const getUrlSearchParams = (param: string = ''): URLSearchParams | string => {
    const locHref = locHrefUtil.getLocHref();
    const searchParams = new URL(locHref).searchParams;
    return param ? searchParams.get(param) || '' : searchParams;
};

export const redirectToSalesView = (dealId: string = ''): void => {
    const dealXgId = offerSessionUtils.getDealXgId() || dealId;
    if (dealXgId) {
        const url = buildShopperUrl(dealXgId);
        window.open(url, '_self');
    } else {
        console.warn('[navigationMiddleware] REDIRECT_TO_SALES_VIEW — dealXgId not found in URL, skipping redirect');
    }
};
